import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Constants from '../Utilities/Constants';
import GetCustomer from '../Components/GetCustomer';

const AppContext = createContext();

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};

export const AppProvider = ({ children }) => {
    // Data state
    const [site, setSite] = useState(null);
    const [units, setUnits] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    
    // App state
    const [cart, setCart] = useState([]);
    const [cartId, setCartId] = useState();
    const [customer, setCustomer] = useState();
    
    // Login modal state
    const [showLoginModal, setShowLoginModal] = useState(false);

    // Payment result (from payment hub / polling)
    const [paymentResult, setPaymentResultState] = useState(() => {
        try {
            const stored = sessionStorage.getItem('paymentResult');
            return stored ? JSON.parse(stored) : null;
        } catch (e) {
            return null;
        }
    });

    const setPaymentResult = (result, persist = true) => {
        setPaymentResultState(result);
        try {
            if (persist && result) {
                sessionStorage.setItem('paymentResult', JSON.stringify(result));
            } else {
                sessionStorage.removeItem('paymentResult');
            }
        } catch (e) {
            // ignore storage errors
        }
    };

    // Initialize site
    useEffect(() => {
        const fetchSite = async () => {
            try {
                const response = await axios.get(Constants.API_URL_GET_SITE, { timeout: 5000 });
                setSite(response.data);

            } catch (error) {
                console.error('Error fetching site:', error);
                // Set fallback site data to prevent hanging
                setSite({ 
                    siteName: 'SuperCommerce', 
                    logo: null 
                });
            }
        };
    
        fetchSite();
    }, []);

    // Initialize units data
    useEffect(() => {
        const fetchUnits = async () => {
            try {
                const response = await axios.get(Constants.API_URL_GET_UNITS);
                setUnits(response.data);
                setIsInitialized(true);
            } catch (error) {
                console.error('Error fetching units:', error);
                setIsInitialized(true);
            }
        };

        fetchUnits();
    }, []);

    // Enrich a product with units - NO cart dependency
    const enrichProduct = useCallback((product, activeUnitId = null) => {
        if (!isInitialized || !units.length) {
            return { ...product, units: [] };
        }
        
        // Build enriched units array
        const enrichedUnits = [];
        
        // If no conversion factor, only the product base unit is offered
        const saleUnitIds = product.convert
            ? [product.saleUnitId1, product.saleUnitId2]
            : [product.unitId];

        saleUnitIds
            .filter(unitId => unitId != null)
            .forEach((unitId, index) => {
                const unit = units.find(u => u.unitId === unitId);
                if (unit) {
                    let isActive = activeUnitId !== null ? unit.unitId === activeUnitId : index === 0;
                    enrichedUnits.push({ ...unit, active: isActive });
                }
            });

        // Build the enriched product
        const productUnit = units.find(u => u.unitId === product.unitId) || null;
        return {
            ...product,
            unit: productUnit,
            units: enrichedUnits
        };
    }, [isInitialized, units]); // No cart dependency!

    // Enhanced setCart that enriches items
    const setEnrichedCart = useCallback((newCartItems) => {
        if (!newCartItems) {
            setCart([]);
            return;
        }
        const enrichedItems = newCartItems.map(item => {
            if (!item || !item.product) return item;
            
            // Enrich product with cart item's active unit
            const enrichedProduct = enrichProduct(item.product, item.unitId);
            return {
                ...item,
                product: {
                    ...enrichedProduct,
                    cart: item.itemQtty
                }
            };
        });

        setCart(enrichedItems);
    }, [enrichProduct]);

    // Initialize customer and cart after units are loaded
    useEffect(() => {
        if (isInitialized) {
            GetCustomer({
                setCustomer, 
                setCart: setEnrichedCart, // Use enriching version
                setCartId,
                enrichProduct
            });
        }
    }, [isInitialized, setEnrichedCart, enrichProduct]);

    const value = {
        // Data properties
        site,
        units,
        isInitialized,
        isReady: isInitialized,
        enrichProduct,

        // App properties
        customer,
        setCustomer,
        cart, // Already enriched
        setCart: setEnrichedCart, // ProductActions will call this
        cartId,
        setCartId,
        
        // Login modal
        showLoginModal,
        setShowLoginModal,

        // Payment result shared state
        paymentResult,
        setPaymentResult
    };

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};

// Export the context for direct access if needed
export { AppContext };

// For backward compatibility, also export as AppContextProvider
export const AppContextProvider = AppProvider;